// ---- GEAR UPGRADE: fixed rarity + rolled stat VALUES (the "Gear Upgrade" modal) ---------------------------
// Every catalogue piece has a FIXED RARITY set by its tier — the ladder every player climbs (tier 1 = common …
// tier 8 = legendary). Rarity is NOT upgraded by the player; it comes with the piece you buy. Rarity decides:
//   • how many stat LINES the piece has (common 1 → legendary 5), and
//   • the per-rarity multiplier on the rolled value range (higher tier/rarity → bigger stat values).
// The player still RE-ROLLS the stat VALUES (and, for an unlocked line, which STAT it is) for gold — the value
// range scales with the player's fishing LEVEL × the piece's rarity multiplier — and can LOCK a line's stat
// TYPE (only its value rerolls) and/or FREEZE its value (whole line frozen), each a one-time fee.
// The rolled stats REPLACE the old static per-tier `stats` when deriving the player's fishing stats.

import type { FishingStatId, GearSlot } from "./fishingGear";
import type { Rarity } from "./insects";

export const GEAR_RARITIES: Rarity[] = ["common", "uncommon", "rare", "epic", "legendary"];

/** How many stat lines a piece has at each rarity. */
export const RARITY_STAT_COUNT: Record<Rarity, number> = {
  common: 1, uncommon: 2, rare: 3, epic: 4, legendary: 5,
};

/** Per-rarity multiplier on the rolled value range — higher rarity (= higher tier) rolls bigger stats.
 *  Compressed from 1.0/1.5/2.1/2.8/3.6: a legendary is still comfortably the best piece, but the spread no
 *  longer compounds with the LINE COUNT (a legendary already gets 5 lines to a common's 1, so multiplying the
 *  per-line value by 3.6 on top made one legendary worth 18 commons). See `rollRange`. */
export const RARITY_VALUE_MULT: Record<Rarity, number> = {
  common: 1.0, uncommon: 1.35, rare: 1.75, epic: 2.2, legendary: 2.7,
};

/** Display colour per rarity (matches the game's rarity palette elsewhere). */
export const RARITY_COLOR: Record<Rarity, string> = {
  common: "#b9c2d0", uncommon: "#5fd06a", rare: "#4aa3ff", epic: "#b06bff", legendary: "#ffb43a",
};

/** The FIXED rarity of a piece by its TIER (1..8). The ladder ends at legendary (tier 8), so every player
 *  climbs the same rarity progression as they unlock higher-level gear. */
export const TIER_RARITY: Rarity[] = [
  "common", "common", "uncommon", "uncommon", "rare", "rare", "epic", "legendary",
];

/** The rarity of a catalogue piece from its id (e.g. `rod5` → tier 5 → "rare"). Lures/starters default common. */
export function pieceRarity(gearItemId: string): Rarity {
  const m = /(\d+)$/.exec(gearItemId);
  const tier = m ? Math.max(1, Math.min(8, parseInt(m[1]!, 10))) : 1;
  return TIER_RARITY[tier - 1] ?? "common";
}

/** Which STAT TYPES each slot can roll — ALL stats are available (a piece can roll anything), but the slot's
 *  PRIMARY stat leads the list so it's the most likely / the piece's identity (rod→line strength, reel→power,
 *  handle→intuition, hook→bite rate; luck is the wildcard). A high-rarity piece with many lines can pick up
 *  varied stats without duplicating, but steers into its strength. */
export const SLOT_STAT_POOL: Record<GearSlot, FishingStatId[]> = {
  rod:    ["lineStrength", "luck", "power", "biteRate", "intuition"],
  reel:   ["power", "luck", "lineStrength", "biteRate", "intuition"],
  handle: ["intuition", "luck", "biteRate", "lineStrength", "power"],
  hook:   ["biteRate", "luck", "intuition", "power", "lineStrength"],
  lure:   ["luck", "intuition", "biteRate", "power", "lineStrength"],
};

/** One rolled stat line on a piece: its stat id, current value, and lock flags. */
export interface GearStatLine {
  id: FishingStatId;
  value: number;
  lockType?: boolean;    // paid once → this line keeps its stat TYPE across rerolls (value still rerolls)
  lockValue?: boolean;   // paid once → this line is fully FROZEN (implies lockType)
}

/** The rolled state of one gear piece (keyed by gear item id in the save). */
export interface GearRoll {
  rarity: Rarity;
  stats: GearStatLine[];
}

// ---- roll value range (level × rarity) --------------------------------------------------------------------

/**
 * The [min,max] a single stat line can roll at a given fishing LEVEL + piece RARITY. Level widens the band;
 * rarity multiplies it. e.g. lvl 10 common ≈ [2,4], lvl 50 legendary ≈ [24,57].
 *
 * WHY THESE ARE SMALL. It is easy to read one line's range and think it looks weak — the number that matters
 * is not the line, it is the 25 of them a fully-kitted player wears. Five slots × five legendary lines all
 * roll from this band, and the slot pools overlap, so the lines pile onto the same handful of stats. At the
 * old 0.5/1.2 per level a single L50 legendary line rolled up to 216 points against a stat curve that was
 * 97% exhausted by 200 — one line very nearly maxed a stat by itself, and the full kit reached ~765 points
 * per stat, four times past the point where anything further did nothing. Levelling then moved 0.000%.
 *
 * The coefficients below put a full L50 legendary kit at ~200 gear points per stat, which sits around 80% of
 * the way up the (also stretched — see `statEffect`) curve: clearly strong, still short of the ceiling, and
 * still leaving room for a level-up to register. Change these and you change how much headroom the curve has
 * left; they are tuned against `statEffect`'s scales and the two must move together.
 */
const ROLL_MIN_PER_LEVEL = 0.18;
const ROLL_MAX_PER_LEVEL = 0.42;
export function rollRange(level: number, rarity: Rarity): { min: number; max: number } {
  const lv = Math.max(1, level);
  const mult = RARITY_VALUE_MULT[rarity];
  const min = Math.round(lv * ROLL_MIN_PER_LEVEL * mult);
  const max = Math.round(lv * ROLL_MAX_PER_LEVEL * mult);
  return { min: Math.max(1, min), max: Math.max(min + 1, max) };
}

/** A deterministic-enough roll in [min,max] using a caller-supplied unit random. */
function rollValue(level: number, rarity: Rarity, rnd: () => number): number {
  const { min, max } = rollRange(level, rarity);
  return Math.round(min + rnd() * (max - min));
}

// ---- costs ------------------------------------------------------------------------------------------------

/** Gold to RE-ROLL a piece (scales with level + rarity so late-game rerolls cost more). */
export function rerollCost(level: number, rarity: Rarity): number {
  const rIdx = GEAR_RARITIES.indexOf(rarity);
  return Math.round((60 + level * 12) * (1 + rIdx * 0.6));
}

/** One-time fee to LOCK a line's stat TYPE (keep the stat, still reroll its value). */
export function lockTypeCost(level: number, rarity: Rarity): number {
  const rIdx = GEAR_RARITIES.indexOf(rarity);
  return Math.round((300 + level * 30) * (1 + rIdx * 0.5));
}

/**
 * THE LOCK CAP — at most this many lines of one piece may be locked (a type lock or a freeze — either counts).
 * Without it a player with enough gold could freeze every line once each had rolled its maximum, and the
 * piece would never be gambled on again; with it, a legendary always rerolls at least two of its five lines.
 * An existing piece already over the cap keeps the locks it paid for (they unlock freely) but takes no more.
 */
export const MAX_LOCKED_LINES = 3;
/** How many of a piece's lines carry a lock of either kind. */
export function lockedLineCount(roll: Pick<GearRoll, "stats">): number {
  return roll.stats.filter((s) => s.lockType || s.lockValue).length;
}
/** May line `i` take a NEW lock? A line that is already locked can always deepen (type → freeze) or unlock;
 *  an unlocked line needs the piece to be under the cap. */
export function canLockLine(roll: Pick<GearRoll, "stats">, i: number): boolean {
  const s = roll.stats[i];
  if (!s) return false;
  return !!(s.lockType || s.lockValue) || lockedLineCount(roll) < MAX_LOCKED_LINES;
}

/** One-time fee to LOCK a line's VALUE too (freeze the whole line). Costs more than the type lock. */
export function lockValueCost(level: number, rarity: Rarity): number {
  return Math.round(lockTypeCost(level, rarity) * 2.2);
}

// ---- operations (pure: state in → new state out) ----------------------------------------------------------

/** Pick a stat from `pool`, WEIGHTED toward the front (the slot's primary stat), so a piece steers into its
 *  strength while still able to roll anything. `used` avoids duplicates. Weight of the i-th entry ∝ 1/(i+1)^1.6. */
function pickWeighted(pool: FishingStatId[], used: Set<FishingStatId>, rnd: () => number): FishingStatId {
  const free = pool.filter((p) => !used.has(p));
  const cand = free.length ? free : pool;
  const w = cand.map((_, i) => 1 / Math.pow(i + 1, 1.6));
  const sum = w.reduce((a, b) => a + b, 0);
  let x = rnd() * sum;
  for (let i = 0; i < cand.length; i++) { if ((x -= w[i]!) <= 0) return cand[i]!; }
  return cand[cand.length - 1]!;
}

/** The DEFAULT roll for a freshly-obtained piece: its FIXED rarity (from the piece's tier) with that rarity's
 *  number of stat lines, each a fresh (distinct) stat WEIGHTED toward the slot's primary, at a level-rolled value. */
export function defaultRoll(slot: GearSlot, rarity: Rarity, level: number, rnd: () => number): GearRoll {
  const pool = SLOT_STAT_POOL[slot];
  const count = RARITY_STAT_COUNT[rarity];
  const used = new Set<FishingStatId>();
  const stats: GearStatLine[] = [];
  for (let i = 0; i < count; i++) {
    const id = pickWeighted(pool, used, rnd);
    used.add(id);
    stats.push({ id, value: rollValue(level, rarity, rnd) });
  }
  return { rarity, stats };
}

/** RE-ROLL a piece's stats. The piece's RARITY is fixed (passed in) and its line count is enforced (a fresh
 *  or migrated piece is topped up to the rarity's count). Locked-VALUE lines are untouched; locked-TYPE lines
 *  keep their id but reroll the value; fully-unlocked lines reroll BOTH the stat id (avoiding dupes) and value. */
/** Does this rarity carry EVERY stat (legendary: 5 lines of a 5-stat pool)? Then a line's stat TYPE is not a
 *  choice — the piece has all of them — so a reroll changes VALUES only and there is no type to lock. */
export function carriesEveryStat(slot: GearSlot, rarity: Rarity): boolean {
  return RARITY_STAT_COUNT[rarity] >= SLOT_STAT_POOL[slot].length;
}

export function rerollStats(roll: GearRoll, slot: GearSlot, rarity: Rarity, level: number, rnd: () => number): GearRoll {
  const pool = SLOT_STAT_POOL[slot];
  const count = RARITY_STAT_COUNT[rarity];
  // start from the existing lines, padded/truncated to the rarity's line count.
  const base: GearStatLine[] = roll.stats.slice(0, count);
  while (base.length < count) base.push({ id: pool[0]!, value: 0 });
  // EVERY STAT ON THE PIECE (legendary): the types stay put and only the values reroll — shuffling which line
  // holds which stat changed nothing. A roll that somehow carries a duplicate (an older save) is repaired to
  // one line per stat first, so the piece really does have all five.
  if (carriesEveryStat(slot, rarity)) {
    const seen = new Set<FishingStatId>();
    const missing = pool.filter((id) => !base.some((s) => s.id === id));
    const fixed = base.map((s) => {
      if (seen.has(s.id) && !s.lockValue && missing.length) return { ...s, id: missing.shift()! };
      seen.add(s.id);
      return s;
    });
    return { rarity, stats: fixed.map((s) => (s.lockValue ? { ...s } : { ...s, value: rollValue(level, rarity, rnd) })) };
  }
  const used = new Set<FishingStatId>();
  for (const s of base) if (s.lockType || s.lockValue) used.add(s.id);
  const stats = base.map((s): GearStatLine => {
    if (s.lockValue) return { ...s };                       // frozen line
    if (s.lockType) return { ...s, value: rollValue(level, rarity, rnd) };   // keep type, reroll value
    const id = pickWeighted(pool, used, rnd);
    used.add(id);
    return { id, value: rollValue(level, rarity, rnd) };
  });
  return { rarity, stats };
}

/**
 * CARRY a slot's roll FORWARD onto a new tier's piece — what the rig UPGRADE TRACK does when a slot moves up
 * a rung, and what the save migration does when it collapses a slot's owned pieces down to its highest tier.
 *
 * The point of the upgrade track is that the piece LEVELS UP rather than being replaced, so everything the
 * player PAID for survives: each existing line keeps its stat id, its rolled value, and its `lockType` /
 * `lockValue` flags. Only the RARITY is re-derived — it is fixed by the new tier (`TIER_RARITY`), never
 * chosen — and rarity owns the LINE COUNT.
 *
 * WHAT HAPPENS TO A LINE COUNT THAT GROWS: the new line ROLLS FRESH. A rare (3 lines) upgraded to epic (4)
 * keeps its three lines exactly as they were and rolls a fourth at the new rarity's value band, picking a
 * stat it does not already carry. The fresh line is unlocked, so a player who wants a different stat on it
 * pays the normal reroll rather than getting a free re-spin of the three they already bought. A count that
 * SHRINKS cannot happen on the track (rarity never goes down with tier) but is handled anyway: the extra
 * lines are truncated from the end.
 */
export function carryRollForward(roll: GearRoll, slot: GearSlot, rarity: Rarity, level: number, rnd: () => number): GearRoll {
  const pool = SLOT_STAT_POOL[slot];
  const count = RARITY_STAT_COUNT[rarity];
  const stats: GearStatLine[] = roll.stats.slice(0, count).map((s) => ({ ...s }));
  const used = new Set<FishingStatId>(stats.map((s) => s.id));
  while (stats.length < count) {
    const id = pickWeighted(pool, used, rnd);
    used.add(id);
    stats.push({ id, value: rollValue(level, rarity, rnd) });
  }
  return { rarity, stats };
}

/** The stat POINTS a rolled piece contributes (sum of its lines) — replaces the old static `stats`. */
export function rollStatPoints(roll: GearRoll): Partial<Record<FishingStatId, number>> {
  const out: Partial<Record<FishingStatId, number>> = {};
  for (const s of roll.stats) out[s.id] = (out[s.id] ?? 0) + s.value;
  return out;
}

// ---- persistence ------------------------------------------------------------------------------------------

/** Coerce the saved per-piece roll map (gearId → GearRoll). Defensive against malformed data. */
export function coerceGearRolls(raw: unknown): Record<string, GearRoll> {
  const out: Record<string, GearRoll> = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!v || typeof v !== "object") continue;
    const o = v as { rarity?: unknown; stats?: unknown };
    const rarity = (GEAR_RARITIES as string[]).includes(o.rarity as string) ? o.rarity as Rarity : "common";
    const statsRaw = Array.isArray(o.stats) ? o.stats : [];
    const validIds: FishingStatId[] = ["biteRate", "lineStrength", "power", "luck", "intuition"];
    // migrate old stat ids: reelPower→power, hookWindow→intuition (its nearest surviving relative).
    const migrateStat = (id: string): string => id === "reelPower" ? "power" : id === "hookWindow" ? "intuition" : id;
    const stats: GearStatLine[] = [];
    for (const s of statsRaw) {
      if (!s || typeof s !== "object") continue;
      const ss = s as { id?: unknown; value?: unknown; lockType?: unknown; lockValue?: unknown };
      const mid = migrateStat(ss.id as string);
      if (!validIds.includes(mid as FishingStatId)) continue;
      ss.id = mid;
      if (typeof ss.value !== "number" || !Number.isFinite(ss.value)) continue;
      stats.push({
        id: ss.id as FishingStatId, value: Math.max(0, Math.round(ss.value)),
        ...(ss.lockType === true ? { lockType: true } : {}),
        ...(ss.lockValue === true ? { lockValue: true, lockType: true } : {}),
      });
    }
    if (stats.length) out[id] = { rarity, stats };
  }
  return out;
}
